<template>
  <div class="actions-toolbar mb-4 sticky top-0 z-9 bg-primary6">
    <div class="actions-toolbar__nav bg-base rounded-2 pa-4 pb-4 flex flex-col gap-3">
      <div class="flex justify-between items-center flex-wrap">
        <div class="breadcrumb-area flex justify-between gap-2 items-center truncate">
          <NBreadcrumb class="overflow-x-auto" separator=">">
            <NBreadcrumbItem
              class="cursor-pointer"
              :class="mode === 'selector' ? 'text-md' : 'text-lg tracking-[.25px]'"
              :style="{
                '--n-font-size': mode === 'selector' ? '16px' : '18px',
              }"
              @click="onRootBreadcrumbClick"
            >
              {{
                store.state.viewMode === 'sections'
                  ? t('canvasUI.browserApp.sectionSmartFolders.allSections')
                  : t('canvasUI.browserApp.sectionSmartFolders.allSmartFolders')
              }}
            </NBreadcrumbItem>
            <NBreadcrumbItem
              v-for="(segment, index) in store.state.currentPath"
              :key="index"
              :class="[
                mode === 'selector' ? 'text-md' : 'text-lg tracking-[.25px]',
                { 'cursor-pointer ': index !== store.state.currentPath.length - 1 },
              ]"
              :clickable="index !== store.state.currentPath.length - 1"
              @click="onBreadcrumbClick(index)"
            >
              <div :class="mode === 'selector' ? 'text-md' : 'text-lg tracking-[.25px]'">
                {{ segment.property.display_name }}:
                <span class="font-bold">
                  {{ Array.isArray(segment.value) ? segment.value.join(', ') : segment.value }}
                </span>
              </div>
            </NBreadcrumbItem>
          </NBreadcrumb>
        </div>
        <div class="flex justify-between items-center ml-auto gap-2">
          <CButton
            v-tooltip="t('canvasUI.components.sections.CSectionManagementToolbar.normalView')"
            circle
            icon
            icon-placement="left"
            @click="toggleSmartFoldersView"
          >
            <template #icon>
              <CIcon icon="table-list" size="16px" />
            </template>
          </CButton>
          <CButton
            v-if="
              (store.state.viewMode === 'sections' && !isLastLevel) ||
              (store.state.viewMode !== 'sections' && !isRootLevel)
            "
            v-tooltip="
              store.state.viewMode === 'sections'
                ? t('canvasUI.components.sections.CSectionManagementToolbar.viewAsFolders')
                : t('canvasUI.components.sections.CSectionManagementToolbar.viewAsSections')
            "
            circle
            icon
            icon-placement="left"
            :type="store.state.viewMode === 'sections' ? 'primary' : 'default'"
            @click="onToggleViewMode"
          >
            <template #icon>
              <CIcon :icon="store.state.viewMode === 'sections' ? 'folder' : 'file'" size="16px" />
            </template>
          </CButton>

          <CDropdown v-if="shouldShowSort" :options="sortOptions" trigger="click" @select="onSortSelect">
            <CButton type="default">
              {{ currentSortLabel }}
              <template #icon>
                <CIcon :icon="currentSortIcon" size="16px" />
              </template>
            </CButton>
          </CDropdown>
        </div>
      </div>

      <!-- Search Field and Selection Actions -->
      <div class="search-area flex justify-between items-center gap-2">
        <CSearch
          v-model:search="searchValue"
          class="max-w-md"
          :placeholder="t('canvasUI.browserApp.sectionSmartFolders.searchByName')"
          show-icon
          @update:search="onSearchInput"
        />
        <CSectionSelectionActions
          v-if="mode === 'selector'"
          :added-item-ids="addedItemIds"
          :selected-item-ids="selectedItemIds"
          :total-selected="selectedItemIds?.length || 0"
          @clear-selection="emit('clear-selection')"
          @items-added="(itemIds, position) => emit('items-added', itemIds, position)"
          @items-removed="(itemIds) => emit('items-removed', itemIds)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="tsx">
import { computed, inject, type ComputedRef } from 'vue'
import { NBreadcrumb, NBreadcrumbItem } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import CSectionSelectionActions from '@lib/components/CSectionSelectionActions/CSectionSelectionActions.vue'
import useSectionSmartFolderStore from '@lib/stores/sectionSmartFolderStore'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

type Props = {
  search?: string
  currentSort?: { field: string; order: 'asc' | 'desc' }
  mode: 'management' | 'selector'
  selectedItemIds?: string[]
  addedItemIds?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  search: '',
  currentSort: () => ({ field: 'name', order: 'asc' }),
  selectedItemIds: () => [],
  addedItemIds: () => [],
})

const emit = defineEmits<{
  'breadcrumb-click': [index: number]
  'breadcrumb-reset': []
  search: [value: string]
  'update:search': [value: string]
  'sort-changed': [sort: { field: string; order: 'asc' | 'desc' }]
  'clear-selection': []
  'items-added': [itemIds: string[], position?: 'top' | 'bottom']
  'items-removed': [itemIds: string[]]
}>()

const { t } = useI18n()
const store = useSectionSmartFolderStore()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const shouldShowSort = computed(() => launchDarkly.value.enable_sorting_in_sections)

const searchValue = computed({
  get: () => props.search,
  set: (value: string) => emit('update:search', value),
})

const isLastLevel = computed(
  () =>
    store.state.currentPath &&
    store.state.hierarchyLevels &&
    store.state.currentPath.length === store.state.hierarchyLevels.length,
)

const isRootLevel = computed(() => !store.state.currentPath?.length)

function toggleSmartFoldersView() {
  store.isSmartFoldersView.value = false
  emit('clear-selection')
}

function onRootBreadcrumbClick() {
  store.actions.navigateToLevel(0)
  emit('breadcrumb-reset')
}

function onBreadcrumbClick(index: number) {
  emit('breadcrumb-click', index)
}

function onToggleViewMode() {
  // Clear selections when view mode changes (context changes)
  emit('clear-selection')

  store.actions.toggleViewMode()
}

function onSearchInput(value: string) {
  emit('search', value)
}

const sortOptions = computed<DropdownMixedOption[]>(() => [
  {
    label: () => <div class="font-size-4">{t('canvasUI.browserApp.sectionSmartFolders.sortByDefault')}</div>,
    key: sortOptionConfig.default.key,
    icon: () => <CIcon icon={sortOptionConfig.default.icon} />,
  },
  {
    label: () => <div class="font-size-4">{t('canvasUI.browserApp.sectionSmartFolders.sortByNameAsc')}</div>,
    key: sortOptionConfig.name.asc.key,
    icon: () => <CIcon icon={sortOptionConfig.name.asc.icon} />,
  },
  {
    label: () => <div class="font-size-4">{t('canvasUI.browserApp.sectionSmartFolders.sortByNameDesc')}</div>,
    key: sortOptionConfig.name.desc.key,
    icon: () => <CIcon icon={sortOptionConfig.name.desc.icon} />,
  },
  ...(store.state.viewMode === 'sections'
    ? [
        {
          label: () => <div class="font-size-4">{t('canvasUI.browserApp.sectionSmartFolders.sortBySequenceAsc')}</div>,
          key: sortOptionConfig.sequence.asc.key,
          icon: () => <CIcon icon={sortOptionConfig.sequence.asc.icon} />,
        },
        {
          label: () => <div class="font-size-4">{t('canvasUI.browserApp.sectionSmartFolders.sortBySequenceDesc')}</div>,
          key: sortOptionConfig.sequence.desc.key,
          icon: () => <CIcon icon={sortOptionConfig.sequence.desc.icon} />,
        },
        {
          label: () => <div class="font-size-4">{t('canvasUI.browserApp.sectionSmartFolders.sortByModifiedAsc')}</div>,
          key: sortOptionConfig.modified_at.asc.key,
          icon: () => <CIcon icon={sortOptionConfig.modified_at.asc.icon} />,
        },
        {
          label: () => <div class="font-size-4">{t('canvasUI.browserApp.sectionSmartFolders.sortByModifiedDesc')}</div>,
          key: sortOptionConfig.modified_at.desc.key,
          icon: () => <CIcon icon={sortOptionConfig.modified_at.desc.icon} />,
        },
      ]
    : []),
])

const sortOptionConfig = {
  default: {
    key: 'default',
    icon: 'list',
  },
  name: {
    asc: {
      key: 'name_asc',
      icon: 'arrow-down-a-z',
    },
    desc: {
      key: 'name_desc',
      icon: 'arrow-down-z-a',
    },
  },
  sequence: {
    asc: {
      key: 'sequence_asc',
      icon: 'arrow-down-1-9',
    },
    desc: {
      key: 'sequence_desc',
      icon: 'arrow-down-9-1',
    },
  },
  modified_at: {
    asc: {
      key: 'modified_at_asc',
      icon: 'calendar-arrow-up',
    },
    desc: {
      key: 'modified_at_desc',
      icon: 'calendar-arrow-down',
    },
  },
}

const currentSortKey = computed(() => {
  if (props.currentSort?.field === 'default') {
    return 'default'
  }
  return `${props.currentSort?.field || 'name'}_${props.currentSort?.order || 'asc'}`
})

const currentSortIcon = computed(() => {
  if (currentSortKey.value === 'default') {
    return sortOptionConfig.default.icon
  }

  const lastUnderscoreIndex = currentSortKey.value.lastIndexOf('_')
  const field = currentSortKey.value.substring(0, lastUnderscoreIndex)
  const order = currentSortKey.value.substring(lastUnderscoreIndex + 1) as 'asc' | 'desc'

  const fieldConfig = sortOptionConfig[field as keyof typeof sortOptionConfig]
  if (fieldConfig && 'asc' in fieldConfig && 'desc' in fieldConfig) {
    return fieldConfig[order]?.icon || 'arrow-down-wide-short'
  }
  return 'arrow-down-wide-short'
})

const currentSortLabel = computed(() => {
  if (currentSortKey.value === 'default') {
    return `${t('canvasUI.common.sortBy')}: ${t('canvasUI.browserApp.sectionSmartFolders.default')}`
  }

  const lastUnderscoreIndex = currentSortKey.value.lastIndexOf('_')
  const field = currentSortKey.value.substring(0, lastUnderscoreIndex)
  const order = currentSortKey.value.substring(lastUnderscoreIndex + 1) as 'asc' | 'desc'

  const fieldLabelMap: Record<string, string> = {
    sequence: t('canvasUI.browserApp.sectionSmartFolders.sequence'),
    name: t('canvasUI.browserApp.sectionSmartFolders.name'),
    modified_at: t('canvasUI.browserApp.sectionSmartFolders.modifiedDate'),
  }

  const orderLabelMap: Record<string, string> = {
    asc: t('canvasUI.common.asc'),
    desc: t('canvasUI.common.desc'),
  }

  if (fieldLabelMap[field] && orderLabelMap[order]) {
    return `${t('canvasUI.common.sortBy')}: ${fieldLabelMap[field]} (${orderLabelMap[order]})`
  }

  return t('canvasUI.common.sort')
})

function onSortSelect(key: string) {
  if (key === 'default') {
    emit('sort-changed', { field: 'default', order: 'asc' })
    return
  }

  const lastUnderscoreIndex = key.lastIndexOf('_')
  const field = key.substring(0, lastUnderscoreIndex)
  const order = key.substring(lastUnderscoreIndex + 1) as 'asc' | 'desc'

  emit('sort-changed', { field, order })
}
</script>
